<template>
  <component
    :is="parentComponent"
    v-bind="parentComponentAttributes"
    :class="['option-content', styleClass]"
    :style="styles"
    @[clickAvailable]="selectDate($event)"
    @[mouseoverAvailable]="addThemedHoverStyle"
    @[mouseoutAvailable]="removeThemedHoverStyle"
    @[focusinAvailable]="addThemedHoverStyle"
    @[focusoutAvailable]="removeThemedHoverStyle"
  >
    <div
      v-if="!!dateObject"
      class="label"
    >
      <span
        v-for="(datePart, key) in dateObject"
        :key="key"
        :class="key"
      >{{ datePart }}</span>
    </div>
    <div
      v-else
      class="label"
    >
      {{ deliveryOption.label }}
    </div>
    <div
      v-if="shouldDisplayPrice"
      class="additional-info price"
    >
      {{ i18n('price', { price: $n(deliveryOption.price, 'currency') }) }}
    </div>
    <div
      v-if="shouldDisplayFlexMessaging"
      class="additional-info flex-messaging"
    >
      {{ deliveryOption.flexBackupFeeLabel }}
    </div>
    <div
      v-if="shouldDisplayAltProductsLink"
      class="additional-info"
    >
      <span class="sr-only">{{ i18n('unavailable') }}</span>
      <a
        class="alt-products-link"
        :style="colorStyle"
        :href="deliveryOption.alternativeUrl"
      >{{ i18n('alternativeProductsLink') }}</a>
    </div>
  </component>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'GridItem',
  props: {
    deliveryOption: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState([
      'primaryColor',
      'isFeeExempt'
    ]),
    isAvailable() {
      return this.deliveryOption.isAvailable;
    },
    isFlex() {
      return this.deliveryOption.isFlex();
    },
    isToday() {
      return this.deliveryOption.isToday();
    },
    parentComponent() {
      return this.isAvailable
        ? 'button'
        : 'div';
    },
    parentComponentAttributes() {
      return this.isAvailable
        ? {
          type: 'button',
          value: this.deliveryOption.value,
          'data-label': this.deliveryOption.orderLabel
        }
        : {}
    },
    clickAvailable() {
      return this.eventAvailable('click');
    },
    mouseoverAvailable() {
      return this.eventAvailable('mouseover');
    },
    mouseoutAvailable() {
      return this.eventAvailable('mouseout');
    },
    focusinAvailable() {
      return this.eventAvailable('focusin');
    },
    focusoutAvailable() {
      return this.eventAvailable('focusout');
    },
    shouldDisplayPrice() {
      return this.hasFee
        && this.isAvailable
        && !this.isFeeExempt;
    },
    shouldDisplayFlexMessaging() {
      return this.isFlex && !this.shouldDisplayPrice;
    },
    shouldDisplayAltProductsLink() {
      return !this.isAvailable && this.deliveryOption.alternativeUrl;
    },
    hasFee() {
      return this.deliveryOption.price > 0;
    },
    styleClass() {
      let klass = '';

      if (!this.isAvailable) {
        klass = 'unavailable';
      } else {
        if (this.isToday) {
          klass = 'today';
        }

        if (this.isFlex) {
          klass = 'flex';
        }
      }

      return klass;
    },
    dateObject() {
      if (this.isFlex) return null;

      const label = this.deliveryOption.label;
      const i = label.indexOf(' ') + 1;
      const dateParts = [label.slice(0, i), label.slice(i)];

      return {
        'weekday': dateParts[0],
        'month-day': dateParts[1]
      }
    },
    styles() {
      switch (this.styleClass) {
        case 'today':
          return {
            'border-color': this.primaryColor,
            'color': this.primaryColor
          }
        case 'flex':
          return {
            'border-color': this.primaryColor,
            'background-color': this.primaryColor,
            'color': '#fff'
          }
        default:
          return null
      }
    },
    colorStyle() {
      return {
        color: this.primaryColor
      };
    }
  },
  methods: {
    i18n(key, values = null) {
      return this.$t(this.i18nPath(key), values);
    },
    i18nPath(key) {
      return `gridItem.${key}`;
    },
    eventAvailable(eventName) {
      return this.isAvailable ? eventName : null;
    },
    selectDate(e) {
      const selection = e.currentTarget;

      this.$emit('date-selected', selection);
    },
    addThemedHoverStyle(e) {
      const option = e.currentTarget;
      option.style.boxShadow = `0 0 0 0.2rem ${this.primaryColor}85`;
    },
    removeThemedHoverStyle(e) {
      const option = e.currentTarget;
      option.style.boxShadow = 'none';
    }
  }
};
</script>

<style lang="scss">
#core-availability {
  .option-content {
    min-height: 4.75rem;
    width: 100%;
    padding: 0 0.2rem;
    border: 3px solid $gray-600;
    background-color: $white;
    color: $gray-600;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: all ease-out 0.15s;

    &:not(.unavailable) {
      &:hover, &:focus {
        box-shadow: $btn-focus-box-shadow;
        outline: 0;
      }
    }

    .label {
      font-weight: bold;
    }

    .additional-info {
      font-size: $font-size-12px;
      line-height: $font-size-12px;
    }

    .alt-products-link {
      @include with-right-double-arrow();
      display: inline-block;
      font-size: $font-size-11px;
      font-weight: bold;
    }

    &.unavailable {
      border-color: $gray-200;
      background-color: $gray-200;
      color: $gray-500;
    }

    &.today {
      border-color: $primary;
      color: $primary;
    }

    &.flex {
      border-color: $primary;
      background-color: $primary;
      color: $white;
    }
  }
}
</style>